/**
 * The two ways a ledger row is read: a one-line summary and the full diff.
 *
 * Extracted from `AuditTimeline` (#1979) because the timeline is no longer the
 * only surface that renders a ledger row — the global change-history list
 * (#1981) shows the same summary against a DIFFERENT `fieldIndex`, since its
 * rows span four resource types instead of one. Both components therefore keep
 * their generic `<T>` and take the index as a parameter rather than deriving
 * it: the index is the caller's knowledge, not theirs.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from '@resource-kit/ui';
import {
  resolveCellWidget,
  IdCell,
  LIST_TRUNCATE,
  JsonKeyValueTable,
} from '@resource-kit/ui/react';
import { UUID_SHAPE } from '@resource-kit/controllers';
import type { EntityField } from '@resource-kit/controllers/react';
import i18n from '@/i18n';
import { JsonDiffPanel } from '@components/ui/JsonDiffPanel';
import { asJsonStructure, structuralJsonDiff, type JsonDiffTree } from '@lib/json-diff';
import type { ResourceAuditResourceType, ResourceAuditTimelineItem } from '@/services';

/** The model's fields indexed by the key an audit row names them with. */
export type FieldIndex<T> = ReadonlyMap<string, EntityField<T>>;

/**
 * Where each audited resource's detail page lives.
 *
 * `null` means this resource type has no detail page: the resource column
 * renders its id without a link. Missing keys remain a compile error.
 */
export const RESOURCE_DETAIL_PATH = {
  orders: '/orders',
  customers: '/customers',
  products: '/products',
  fulfillment_jobs: '/dispatch/jobs',
  tenant_settings: null,
  tenants: null,
} as const satisfies Record<ResourceAuditResourceType, string | null>;

/**
 * Which COLUMNS of a recorded change hold an id, and which record that id
 * names. Read: "in a `fulfillment_jobs` change, the value under `order_id` is
 * an order".
 *
 * A declared table, not a guess. "The value looks like a UUID, so route it
 * somewhere" cannot work — one UUID shape addresses four different tables — and
 * a wrong route is worse than none: it renders as a working link and 404s on
 * click. `auditChangeValueLinks.test.tsx` pins every entry to a column the
 * audited resource actually declares, so a renamed column fails a test instead
 * of quietly dropping the link.
 *
 * Small on purpose. Only ids the ledger really records as INTERNAL uuids
 * belong here: an `orders` snapshot's `customer_id`, for instance, carries a
 * Shopify customer id, which is a different key space and is left as text
 * (verified against prod `resource_audit_logs`, 2026-08-05).
 */
export const AUDITED_ID_PATH = {
  orders: {},
  customers: {},
  products: {},
  fulfillment_jobs: {
    // The job's own primary key: a link back to the record this row is about.
    id: RESOURCE_DETAIL_PATH.fulfillment_jobs,
    // The order it delivers. Both key spaces reach `/orders/:id` — the backend
    // dispatches on shape — but the ledger records the internal uuid.
    order_id: RESOURCE_DETAIL_PATH.orders,
  },
  tenant_settings: {},
  tenants: {},
} as const satisfies Record<ResourceAuditResourceType, Readonly<Record<string, string>>>;

/**
 * The detail route for one audited value, or null when it is not an id we can
 * address.
 *
 * BOTH conditions must hold: the column is declared above AND the value is a
 * UUID. The second guard is what keeps a legacy row — one written before a
 * column held uuids, or holding a Shopify id under the same name — from
 * rendering a link to a record that does not exist.
 */
function auditValueLink(
  resourceType: ResourceAuditResourceType,
  fieldKey: string,
  value: unknown,
): string | null {
  if (typeof value !== 'string' || !UUID_SHAPE.test(value)) return null;
  const columns: Readonly<Record<string, string>> = AUDITED_ID_PATH[resourceType];
  const path = columns[fieldKey];
  return path === undefined ? null : `${path}/${value}`;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * One audited value, rendered by the SAME widget that renders that field in a
 * list or a detail cell: `resolveCellWidget` over `FIELD_TYPE_REGISTRY` — the
 * catalog the column builders resolve through, and therefore the one
 * production actually shows the operator (#1804). An enum reads as its badge
 * and money as formatted money here exactly as it does in the form above.
 *
 * `mode: 'list'` on purpose: a diff row is inline, while the `detail`
 * projection of a json field is a block-level key/value table that would break
 * the line. The field's own `render` override is deliberately NOT consulted —
 * it takes the whole row, and an audit entry carries values without one.
 *
 * An unknown key is not an error. An audit row may name a column the model has
 * since dropped, or one the backend records but no view declares; it degrades
 * to the raw text rather than taking the timeline down.
 */
function AuditValue<T>({
  field,
  fieldKey,
  resourceType,
  value,
}: {
  field: EntityField<T> | undefined;
  fieldKey: string;
  resourceType: ResourceAuditResourceType;
  value: unknown;
}) {
  // An internal uuid ahead of the type projection, because the type is the
  // wrong authority here: `order_id` is declared `text` (its list cell resolves
  // the order NAME off the row, which an audit entry does not carry), so the
  // text projection printed 36 raw characters with no copy button and no route.
  // The SHAPE decides the rendering; the declared table decides the route, and
  // a column with no entry still gets the short id and the copy button.
  if (typeof value === 'string' && UUID_SHAPE.test(value)) {
    const href = auditValueLink(resourceType, fieldKey, value);
    return href === null ? (
      <IdCell value={value} truncate={LIST_TRUNCATE} />
    ) : (
      <IdCell value={value} truncate={LIST_TRUNCATE} linkTo={href} />
    );
  }
  if (!field) return <>{formatValue(value)}</>;
  const Cell = resolveCellWidget(field).Cell;
  return <Cell value={value} mode="list" field={field} />;
}

/** The field's own label, or the raw key when the model does not declare it. */
function auditFieldLabel<T>(field: EntityField<T> | undefined, key: string): string {
  return field ? i18n.t(field.label, { defaultValue: field.label }) : key;
}

export function ChangeSummary<T>({
  entry,
  fieldIndex,
}: {
  entry: ResourceAuditTimelineItem;
  fieldIndex: FieldIndex<T>;
}) {
  const { t } = useTranslation();
  const first = entry.changes[0];
  // Memoised because the change-history list renders fifty of these at once and
  // every one of them would otherwise re-parse two multi-KB payloads on every
  // render of the list. `ChangeRow` has always done this; the summary had not,
  // and it is the one that runs fifty times.
  const jsonDiff = useMemo(
    () => (first ? structuralJsonDiff(first.oldValue, first.newValue) : null),
    [first],
  );

  if (entry.action === 'create') {
    return <span className="text-muted-foreground">{t('audit.recordCreated')}</span>;
  }
  if (entry.action === 'delete') {
    return <span className="text-muted-foreground">{t('audit.recordDeleted')}</span>;
  }
  if (entry.changes.length === 0) {
    return (
      <span className="text-muted-foreground">
        {t(entry.action === 'restore' ? 'audit.recordRestored' : 'audit.recordUpdated')}
      </span>
    );
  }

  const remaining = entry.changes.length - 1;
  const field = fieldIndex.get(first.field);
  const headline = t(
    entry.action === 'restore' ? 'audit.restored.summary' : 'audit.updated.summary',
    { field: auditFieldLabel(field, first.field) },
  );
  const more = remaining > 0 && (
    <span className="text-muted-foreground/60"> ({t('audit.more', { count: remaining })})</span>
  );

  // A JSON column is summarised by HOW MANY of its rows moved. Printing its
  // first value here put a multi-KB payload on the collapsed row — the very
  // thing the expanded diff exists to avoid.
  if (jsonDiff !== null) {
    return (
      <span className="text-muted-foreground">
        {headline}{' '}
        <span className="text-xs text-muted-foreground/70">
          {jsonDiff.changedCount === 0
            ? t('audit.jsonDiff.equivalent')
            : t('audit.jsonDiff.fieldsChanged', { count: jsonDiff.changedCount })}
        </span>
        {more}
      </span>
    );
  }

  return (
    <span className="text-muted-foreground">
      {headline}{' '}
      <span className="text-xs text-muted-foreground/70">
        <AuditValue
          field={field}
          fieldKey={first.field}
          resourceType={entry.resourceType}
          value={first.oldValue}
        />
      </span>{' '}
      {'→'}{' '}
      <span className="text-xs text-foreground">
        <AuditValue
          field={field}
          fieldKey={first.field}
          resourceType={entry.resourceType}
          value={first.newValue}
        />
      </span>
      {more}
    </span>
  );
}

function SnapshotDetails<T>({
  values,
  title,
  fieldIndex,
  resourceType,
  deleted = false,
}: {
  values: Record<string, unknown>;
  title: string;
  fieldIndex: FieldIndex<T>;
  resourceType: ResourceAuditResourceType;
  deleted?: boolean;
}) {
  const entries = Object.entries(values).filter(
    ([, value]) => value !== null && value !== undefined && value !== '',
  );
  if (entries.length === 0) return null;

  return (
    <div className="mt-3 rounded-md border border-border bg-muted/50 p-3">
      <div className="text-xs font-medium text-muted-foreground mb-2">{title}</div>
      <div className="space-y-1">
        {entries.slice(0, 20).map(([key, value]) => {
          // A snapshot carries the same JSON columns an update diffs, and a
          // created product's `raw_data` is just as unreadable on one line as
          // a changed one. There is nothing to diff against here, so it gets
          // the key/value tree rather than the leaf-path table.
          const payload = asJsonStructure(value);
          return (
            <div key={key} className="flex items-baseline gap-2 text-xs">
              {/* No `shrink-0`: `min-w` already floors the width, and the
                  golden parity capture pins this cell's HTML byte for byte. */}
              <span className="font-medium text-foreground min-w-[120px]">
                {auditFieldLabel(fieldIndex.get(key), key)}
              </span>
              {payload === null ? (
                <span
                  className={cn(
                    'break-all',
                    deleted ? 'text-destructive line-through' : 'text-foreground',
                  )}
                >
                  <AuditValue
                    field={fieldIndex.get(key)}
                    fieldKey={key}
                    resourceType={resourceType}
                    value={value}
                  />
                </span>
              ) : (
                <div className="min-w-0 flex-1">
                  <JsonKeyValueTable value={payload} ariaLabel={key} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One recorded field change, in the widest rendering the two values allow.
 *
 * Three cases, decided by SHAPE rather than by a column allowlist — `raw_data`
 * is declared by no frontend schema, so a name list could not reach it, while
 * shape reaches `variants` / `options` / `images` for free:
 *
 *  - both sides a JSON payload → the comparison table;
 *  - one side a payload (a column filled in or cleared) → its key/value tree,
 *    since there is nothing to align it against;
 *  - neither → the inline widget projection this panel always used.
 *
 * The routing question is only "are these two payloads", which `asJsonStructure`
 * answers by parsing — no need to walk the whole tree just to pick a branch.
 * Passing the PARSED sides down also means the table does not parse them again.
 */
function ChangeRow<T>({
  change,
  field,
  resourceType,
}: {
  change: ResourceAuditTimelineItem['changes'][number];
  field: EntityField<T> | undefined;
  resourceType: ResourceAuditResourceType;
}) {
  const { oldValue, newValue } = change;
  const beforeStructure = useMemo(() => asJsonStructure(oldValue), [oldValue]);
  const afterStructure = useMemo(() => asJsonStructure(newValue), [newValue]);
  const bothSides = beforeStructure !== null && afterStructure !== null;
  const oneSided = bothSides ? null : (afterStructure ?? beforeStructure);

  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="font-medium text-foreground min-w-[120px] shrink-0">
        {auditFieldLabel(field, change.field)}
      </span>
      {bothSides ? (
        <JsonDiffPanel
          before={beforeStructure}
          after={afterStructure}
          ariaLabel={change.field}
          className="min-w-0 flex-1"
        />
      ) : oneSided !== null ? (
        <div className="min-w-0 flex-1">
          <JsonKeyValueTable value={oneSided} ariaLabel={change.field} />
        </div>
      ) : (
        <>
          <span className="text-destructive line-through break-all">
            <AuditValue
              field={field}
              fieldKey={change.field}
              resourceType={resourceType}
              value={oldValue}
            />
          </span>
          <span className="text-muted-foreground/60 shrink-0">→</span>
          <span className="text-foreground break-all">
            <AuditValue
              field={field}
              fieldKey={change.field}
              resourceType={resourceType}
              value={newValue}
            />
          </span>
        </>
      )}
    </div>
  );
}

const RAW_DATA_FIELD = 'raw_data';

/**
 * Which JSON-valued change opens by default among several in one entry.
 *
 * `raw_data` is deliberately passed over when a more specific JSON column also
 * changed (owner decision, 2026-08-20): a Shopify product update usually
 * touches `raw_data` incidentally (it mirrors the whole row) alongside a
 * column that says what actually moved, and the specific one is the more
 * useful default. `changedCount > 0` refines that, not reverses it — a JSON
 * column whose diff is serialization-only is never more readable than
 * `raw_data`, so it does not win the default over it.
 */
function defaultExpandedField(
  changes: ResourceAuditTimelineItem['changes'],
  trees: ReadonlyMap<string, JsonDiffTree>,
): string | null {
  const specific = changes.find(
    (change) =>
      change.field !== RAW_DATA_FIELD &&
      trees.has(change.field) &&
      (trees.get(change.field) as JsonDiffTree).changedCount > 0,
  );
  if (specific) return specific.field;

  const rawData = changes.find(
    (change) => change.field === RAW_DATA_FIELD && trees.has(change.field),
  );
  if (rawData) return rawData.field;

  const anyJson = changes.find((change) => trees.has(change.field));
  return anyJson ? anyJson.field : null;
}

/**
 * One JSON-valued change, collapsed to its field label and changed-row count
 * until opened — the accordion member of `ChangeRows`.
 */
function JsonChangeRow<T>({
  change,
  field,
  tree,
  open,
  onToggle,
}: {
  change: ResourceAuditTimelineItem['changes'][number];
  field: EntityField<T> | undefined;
  tree: JsonDiffTree;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
      >
        <ChevronRight className={cn('h-3 w-3 transition-transform', open && 'rotate-90')} />
        <span className="font-medium text-foreground">{auditFieldLabel(field, change.field)}</span>
        <span className="text-muted-foreground/70">
          {tree.changedCount === 0
            ? t('audit.jsonDiff.equivalent')
            : t('audit.jsonDiff.fieldsChanged', { count: tree.changedCount })}
        </span>
      </button>
      {
        // Render the panel whenever the row is open, including when nothing
        // differs. Its empty state is not just the sentence — it still carries
        // the toolbar, and unticking "changed only" there is the only route to
        // the full aligned comparison of a serialization-only change. The
        // header and the panel do both say "no field values changed" then: the
        // header summarises the field, the panel explains its own empty body.
        open && (
          <JsonDiffPanel
            before={tree.root.before}
            after={tree.root.after}
            ariaLabel={change.field}
            className="mt-1"
          />
        )
      }
    </div>
  );
}

/**
 * One entry's change rows, with at most one JSON-valued row's table open at a
 * time.
 *
 * The `useState` lives here rather than in `ExpandedDetails` — that component
 * returns early for create/delete/empty entries, and this one is remounted
 * (`key={entry.id}`) whenever the timeline swaps to a different entry, so the
 * toggle never points at a field the new entry does not have.
 */
function ChangeRows<T>({
  entry,
  fieldIndex,
}: {
  entry: ResourceAuditTimelineItem;
  fieldIndex: FieldIndex<T>;
}) {
  const trees = useMemo(
    () =>
      new Map(
        entry.changes.flatMap((change) => {
          const tree = structuralJsonDiff(change.oldValue, change.newValue);
          return tree ? ([[change.field, tree]] as const) : [];
        }),
      ),
    [entry.changes],
  );
  const [expandedField, setExpandedField] = useState<string | null>(() =>
    defaultExpandedField(entry.changes, trees),
  );

  return (
    <>
      {entry.changes.map((change, index) => {
        const tree = trees.get(change.field);
        if (tree === undefined) {
          return (
            <ChangeRow
              key={`${change.field}-${index}`}
              change={change}
              field={fieldIndex.get(change.field)}
              resourceType={entry.resourceType}
            />
          );
        }
        const open = expandedField === change.field;
        return (
          <JsonChangeRow
            key={`${change.field}-${index}`}
            change={change}
            field={fieldIndex.get(change.field)}
            tree={tree}
            open={open}
            onToggle={() => setExpandedField(open ? null : change.field)}
          />
        );
      })}
    </>
  );
}

export function ExpandedDetails<T>({
  entry,
  fieldIndex,
}: {
  entry: ResourceAuditTimelineItem;
  fieldIndex: FieldIndex<T>;
}) {
  const { t } = useTranslation();
  if (entry.action === 'create' && entry.newValues) {
    return (
      <SnapshotDetails
        values={entry.newValues}
        title={t('audit.initialValues')}
        fieldIndex={fieldIndex}
        resourceType={entry.resourceType}
      />
    );
  }
  if (entry.action === 'delete' && entry.oldValues) {
    return (
      <SnapshotDetails
        values={entry.oldValues}
        title={t('audit.deletedRecord')}
        fieldIndex={fieldIndex}
        resourceType={entry.resourceType}
        deleted
      />
    );
  }
  if (entry.changes.length === 0) return null;

  return (
    <div className="mt-3 rounded-md border border-border bg-muted/50 p-3">
      <div className="text-xs font-medium text-muted-foreground mb-2">{t('audit.changes')}</div>
      <div className="space-y-2">
        <ChangeRows key={entry.id} entry={entry} fieldIndex={fieldIndex} />
      </div>
    </div>
  );
}
